import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import libraryService from '../../services/libraryService';
import classesService from '../../services/classesService';
import DialogModal from '../../components/DialogModal';
import LibraryItemForm from '../../components/library/LibraryItemForm';
import LibraryCatalog from '../../components/library/LibraryCatalog';
import CirculationPanel from '../../components/library/CirculationPanel';
import { Plus } from '@phosphor-icons/react';

function TeacherLibrarySection() {
  const { user } = useAuth();
  const [pageTab, setPageTab] = useState('catalog');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadLookups = useCallback(async () => {
    try {
      const [categoriesData, tagsData, subjectsData, classesData] = await Promise.all([
        libraryService.getCategories(),
        libraryService.getTags(),
        classesService.listAllSubjects().catch(() => []),
        classesService.listClasses().catch(() => []),
      ]);
      setCategories(categoriesData || []);
      setTags(tagsData || []);
      setSubjects(subjectsData || []);
      setClasses(classesData || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load library filters');
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const openCreateForm = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingItem(null);
    setRefreshKey((k) => k + 1);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await libraryService.deleteItem(deleteTarget.id);
      toast.success('Resource deleted');
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className={pageTab === 'catalog' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setPageTab('catalog')}>Catalog</button>
        <button type="button" className={pageTab === 'circulation' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setPageTab('circulation')}>Circulation Desk</button>
      </div>

      {pageTab === 'circulation' && <CirculationPanel />}

      {pageTab === 'catalog' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
              Show only my uploads
            </label>
            <button className="btn btn-primary" type="button" onClick={openCreateForm}>
              <Plus size={16} /> Upload Resource
            </button>
          </div>

          <LibraryCatalog
            categories={categories}
            tags={tags}
            extraParams={{ mine: mineOnly }}
            showManageActions
            canManage={(item) => item.created_by === user?.id}
            onEdit={openEditForm}
            onDelete={setDeleteTarget}
            refreshKey={refreshKey}
          />

          <LibraryItemForm
            isOpen={showForm}
            onClose={() => { setShowForm(false); setEditingItem(null); }}
            onSaved={handleSaved}
            categories={categories}
            subjects={subjects}
            classes={classes}
            editingItem={editingItem}
          />

          <DialogModal
            isOpen={Boolean(deleteTarget)}
            onClose={() => setDeleteTarget(null)}
            title="Confirm delete"
            message={deleteTarget ? `Delete "${deleteTarget.title}"? This action cannot be undone.` : ''}
            type="warning"
            primaryAction={{ label: deleteLoading ? 'Deleting...' : 'Delete', onClick: confirmDelete }}
            secondaryAction={{ label: 'Cancel', onClick: () => setDeleteTarget(null) }}
            showCloseButton={!deleteLoading}
            isLoading={deleteLoading}
            disableCloseWhenLoading={true}
          />
        </>
      )}
    </div>
  );
}

export default TeacherLibrarySection;
